/**
 * Color extraction from rendered backgrounds.
 *
 * Extracts the dominant color spectrum from the active slide's background,
 * whether it's a photo, solid, gradient, generative, or 3D primitive.
 *
 * For images: loads into a canvas, samples pixels, runs median-cut quantization.
 * For data-driven types: resolves accent/swatch colors from constants.
 *
 * Returns an array of { hex, r, g, b, h, s, l } objects, sorted by vibrancy.
 *
 * Session 62 fix: Presigned URL expiry detection + retry-with-refresh.
 * Hetzner S3 returns 403 without CORS headers on expired presigned URLs,
 * causing the browser to report "CORS blocked" instead of "403 expired".
 * This module now checks URL expiry BEFORE fetching and, on failure,
 * requests a fresh presigned URL via resolveAssets() — max 1 retry.
 */

import {
  GRADIENT_BASE_TONES,
  GRADIENT_ACCENT_COLORS,
  FOREST_BG_URLS,
} from '../constants/backgrounds';
import { P3D_FACE_COLORS, P3D_EDGE_COLORS } from '../constants/primitives3d';
import { hexToRgb, hexToHsl, generateHarmony, luminance } from '../constants/colors';
import { PALETTE_MAP, preparePalette } from '../constants/genArt';
import { resolveAssets } from './storageApi';

// ─── RGB ↔ Hex helpers (local, no dependency on colors.js for these) ─

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
    case g: h = ((b - r) / d + 2) / 6; break;
    default: h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

// ─── Vibrancy score (higher = more colorful & visible) ──────────────

function vibrancy(s, l) {
  // Prefer high saturation + lightness away from extremes (not too dark, not too bright)
  const lightPenalty = 1 - Math.abs(l - 55) / 55;
  return s * Math.max(0.1, lightPenalty);
}

// ─── Median-cut color quantization ──────────────────────────────────

function medianCut(pixels, numColors) {
  if (pixels.length === 0) return [];
  if (pixels.length <= numColors) {
    return pixels.map(([r, g, b]) => ({ r, g, b }));
  }

  let boxes = [pixels.slice()];

  while (boxes.length < numColors) {
    // Find box with largest range in any channel
    let bestRange = -1, bestBox = 0, bestCh = 0;

    for (let i = 0; i < boxes.length; i++) {
      if (boxes[i].length < 2) continue;
      for (let ch = 0; ch < 3; ch++) {
        let lo = 255, hi = 0;
        for (const p of boxes[i]) {
          if (p[ch] < lo) lo = p[ch];
          if (p[ch] > hi) hi = p[ch];
        }
        const range = hi - lo;
        if (range > bestRange) {
          bestRange = range;
          bestBox = i;
          bestCh = ch;
        }
      }
    }

    if (bestRange <= 0) break;

    const box = boxes[bestBox];
    box.sort((a, b) => a[bestCh] - b[bestCh]);
    const mid = Math.floor(box.length / 2);
    boxes.splice(bestBox, 1, box.slice(0, mid), box.slice(mid));
  }

  // Average each box → representative color
  return boxes
    .filter(box => box.length > 0)
    .map(box => {
      const n = box.length;
      const r = Math.round(box.reduce((s, p) => s + p[0], 0) / n);
      const g = Math.round(box.reduce((s, p) => s + p[1], 0) / n);
      const b = Math.round(box.reduce((s, p) => s + p[2], 0) / n);
      return { r, g, b };
    });
}

// ─── Extract colors from a canvas image ─────────────────────────────

function extractFromCanvas(img) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Scale down for speed (100×100 = 10k pixels is plenty)
  const maxDim = 100;
  const ratio = Math.min(maxDim / (img.naturalWidth || img.width), maxDim / (img.naturalHeight || img.height));
  const w = Math.max(1, Math.round((img.naturalWidth || img.width) * ratio));
  const h = Math.max(1, Math.round((img.naturalHeight || img.height) * ratio));
  canvas.width = w;
  canvas.height = h;
  ctx.drawImage(img, 0, 0, w, h);

  const data = ctx.getImageData(0, 0, w, h).data;
  const pixels = [];

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a < 128) continue; // skip transparent
    pixels.push([r, g, b]);
  }

  if (pixels.length === 0) return [];

  // Quantize to 8 buckets, then filter/sort
  const raw = medianCut(pixels, 8);
  return raw.map(({ r, g, b }) => {
    const hex = rgbToHex(r, g, b);
    const hsl = rgbToHsl(r, g, b);
    return { hex, r, g, b, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) };
  });
}

// ─── Presigned URL expiry detection ─────────────────────────────────
// Hetzner S3 returns 403 WITHOUT CORS headers on expired presigned URLs,
// so the browser reports "CORS blocked" instead of "403 expired" (Known
// Issue #28). We detect expiry BEFORE fetching to avoid console spam.

/**
 * Check if an S3 presigned URL has expired (or is about to expire).
 * Parses X-Amz-Date + X-Amz-Expires from the query string.
 *
 * @param {string} url — presigned S3 URL
 * @param {number} [safetyMarginMs=60000] — consider expired this many ms before actual expiry
 * @returns {boolean} — true if expired or not a presigned URL we can check
 */
export function isPresignedUrlExpired(url, safetyMarginMs = 60000) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    const amzDate = u.searchParams.get('X-Amz-Date');
    const amzExpires = u.searchParams.get('X-Amz-Expires');
    if (!amzDate || !amzExpires) return false; // not a presigned URL — can't check

    // Parse X-Amz-Date (format: 20260422T163044Z → ISO 8601)
    const dateStr = amzDate.replace(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
      '$1-$2-$3T$4:$5:$6Z'
    );
    const signedAt = new Date(dateStr).getTime();
    if (isNaN(signedAt)) return false;

    const expiresInSec = parseInt(amzExpires, 10);
    if (isNaN(expiresInSec)) return false;

    const expiresAt = signedAt + expiresInSec * 1000;
    return Date.now() > expiresAt - safetyMarginMs;
  } catch {
    return false; // URL parse error — don't block
  }
}

/**
 * Resolve a single asset_id to a fresh presigned download URL.
 * Returns the URL string, or null on failure.
 *
 * @param {string} assetId
 * @returns {Promise<string|null>}
 */
async function refreshPresignedUrl(assetId) {
  if (!assetId) return null;
  try {
    const result = await resolveAssets([assetId]);
    const item = result?.items?.[0];
    if (item?.download_url) return item.download_url;
  } catch (err) {
    console.warn('[colorExtract] URL refresh failed:', err.message);
  }
  return null;
}

// ─── Load an image URL → HTMLImageElement (async) ───────────────────
// Uses fetch() with mode:'cors' to avoid browser cache conflicts.
// When an <img> tag loads the same URL without crossOrigin, Chrome caches
// the response without CORS headers. A subsequent new Image() with
// crossOrigin='anonymous' gets the stale cached response → CORS block.
// fetch() has its own cache namespace and always sends the Origin header.

async function loadImage(url) {
  // Pre-check: reject expired presigned URLs before fetching to avoid
  // CORS console spam (Hetzner S3 returns 403 without CORS headers).
  if (isPresignedUrlExpired(url)) {
    throw new PresignedExpiredError(url);
  }
  const resp = await fetch(url, { mode: 'cors', cache: 'no-store' });
  if (!resp.ok) throw new Error(`Failed to load: ${url} (${resp.status})`);
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to decode image from: ${url}`));
    };
    img.src = objectUrl;
  });
}

/** Sentinel error — distinguishes "URL expired" from network/decode failures. */
class PresignedExpiredError extends Error {
  constructor(url) {
    super(`Presigned URL expired: ${url?.split('?')[0] || url}`);
    this.name = 'PresignedExpiredError';
  }
}

// ─── Extract colors from slide data (type-aware) ────────────────────

/**
 * Extracts the dominant color spectrum from the active slide's background.
 *
 * @param {Object} slide — slide data object
 * @returns {Promise<Array<{hex,r,g,b,h,s,l,vibrancy}>>} — 3-8 dominant colors, sorted by vibrancy desc
 */
export async function extractBackgroundColors(slide) {
  if (!slide) return [];

  // ── 1. Solid background → single color + lighter/darker variants
  if (slide.bgSolid && !slide.gradient?.enabled && !slide.generative?.enabled && !(slide.primitives3d?.enabled)) {
    const hex = slide.bgSolid;
    const rgb = hexToRgb(hex);
    const hsl = hexToHsl(hex);
    return [
      { hex, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) },
    ];
  }

  // ── 2. Gradient → extract base tone swatch + accent hex colors
  if (slide.gradient?.enabled) {
    const results = [];
    const baseTone = GRADIENT_BASE_TONES.find(t => t.id === slide.gradient.base);
    if (baseTone) {
      const rgb = hexToRgb(baseTone.swatch);
      const hsl = hexToHsl(baseTone.swatch);
      results.push({ hex: baseTone.swatch, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
    }
    // Accents are the most visible gradient colors
    for (const accKey of ['accent1', 'accent2']) {
      const accId = slide.gradient[accKey];
      const acc = GRADIENT_ACCENT_COLORS.find(c => c.id === accId);
      if (acc) {
        const hsl = hexToHsl(acc.hex);
        results.push({ hex: acc.hex, r: acc.r, g: acc.g, b: acc.b, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
      }
    }
    return results.sort((a, b) => b.vibrancy - a.vibrancy);
  }

  // ── 3. Generative → extract colors from Canvas2D palette or legacy base/accent
  if (slide.generative?.enabled) {
    const gen = slide.generative;

    // New Canvas2D format — extract colors from the palette
    if (gen.paletteId && PALETTE_MAP[gen.paletteId]) {
      const rawPalette = PALETTE_MAP[gen.paletteId];
      // Apply hue/sat/brightness adjustments (matching what the renderer does)
      const hueShift = gen.hueShift ?? 0;
      const satMul = (gen.saturation ?? 100) / 100;
      const briMul = (gen.brightness ?? 100) / 100;
      const adjusted = preparePalette(rawPalette, hueShift, satMul, briMul);

      const results = [];
      // Background color
      const bgRgb = hexToRgb(adjusted.bgHex);
      const bgHsl = hexToHsl(adjusted.bgHex);
      if (bgRgb && bgHsl) {
        results.push({ hex: adjusted.bgHex, ...bgRgb, ...bgHsl, vibrancy: vibrancy(bgHsl.s, bgHsl.l) });
      }
      // Accent color from palette definition
      const accentHex = rawPalette.accent;
      if (accentHex) {
        const rgb = hexToRgb(accentHex);
        const hsl = hexToHsl(accentHex);
        if (rgb && hsl) {
          results.push({ hex: accentHex, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
        }
      }
      // Sample 3-4 most vibrant palette colors (adjusted)
      for (let i = 0; i < Math.min(4, adjusted.colorsHex.length); i++) {
        const hex = adjusted.colorsHex[i];
        const rgb = hexToRgb(hex);
        const hsl = hexToHsl(hex);
        if (rgb && hsl) {
          results.push({ hex, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
        }
      }
      // Deduplicate by hex
      const seen = new Set();
      const unique = results.filter(c => {
        if (seen.has(c.hex)) return false;
        seen.add(c.hex);
        return true;
      });
      return unique.sort((a, b) => b.vibrancy - a.vibrancy);
    }

    // Legacy SVG format fallback — base tone + accent color
    const results = [];
    const baseTone = GRADIENT_BASE_TONES.find(t => t.id === gen.base);
    if (baseTone) {
      const rgb = hexToRgb(baseTone.swatch);
      const hsl = hexToHsl(baseTone.swatch);
      results.push({ hex: baseTone.swatch, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
    }
    const acc = GRADIENT_ACCENT_COLORS.find(c => c.id === gen.accent);
    if (acc) {
      const hsl = hexToHsl(acc.hex);
      results.push({ hex: acc.hex, r: acc.r, g: acc.g, b: acc.b, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
    }
    return results.sort((a, b) => b.vibrancy - a.vibrancy);
  }

  // ── 4. 3D Primitives → bg tone + face color + edge color
  if (slide.primitives3d?.enabled) {
    const results = [];
    const baseTone = GRADIENT_BASE_TONES.find(t => t.id === slide.primitives3d.bgColor);
    if (baseTone) {
      const rgb = hexToRgb(baseTone.swatch);
      const hsl = hexToHsl(baseTone.swatch);
      results.push({ hex: baseTone.swatch, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
    }
    const face = P3D_FACE_COLORS.find(c => c.id === slide.primitives3d.faceColor);
    if (face) {
      const rgb = hexToRgb(face.hex);
      const hsl = hexToHsl(face.hex);
      results.push({ hex: face.hex, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
    }
    const edge = P3D_EDGE_COLORS.find(c => c.id === slide.primitives3d.edgeColor);
    if (edge) {
      const rgb = hexToRgb(edge.hex);
      const hsl = hexToHsl(edge.hex);
      results.push({ hex: edge.hex, ...rgb, ...hsl, vibrancy: vibrancy(hsl.s, hsl.l) });
    }
    return results.sort((a, b) => b.vibrancy - a.vibrancy);
  }

  // ── 5. Image background → canvas pixel sampling
  //    Session 62: retry-with-refresh for expired presigned URLs.
  //    On failure (CORS from expired signature), resolve a fresh URL via
  //    bgAssetId → resolveAssets(), then retry ONCE. No infinite loops.
  const bgUrl = slide.backgroundUrl || FOREST_BG_URLS[(slide.bgIndex ?? 0) % FOREST_BG_URLS.length];
  if (bgUrl && !bgUrl.startsWith('gradient:')) {
    // Attempt 1: try loading the current URL
    try {
      const img = await loadImage(bgUrl);
      const raw = extractFromCanvas(img);
      return raw.sort((a, b) => b.vibrancy - a.vibrancy).slice(0, 5);
    } catch (e) {
      // If the URL was expired (or fetch failed — likely CORS from expired signature),
      // try to refresh via bgAssetId. Only retry once.
      const bgAssetId = slide?.bgAssetId;
      if (bgAssetId) {
        const freshUrl = await refreshPresignedUrl(bgAssetId);
        if (freshUrl && freshUrl !== bgUrl) {
          try {
            const img = await loadImage(freshUrl);
            const raw = extractFromCanvas(img);
            return raw.sort((a, b) => b.vibrancy - a.vibrancy).slice(0, 5);
          } catch (retryErr) {
            // Retry also failed — give up silently
            return [];
          }
        }
      }
      // No bgAssetId or refresh failed — give up silently (no console spam)
      return [];
    }
  }

  return [];
}

// ─── Pad sparse spectrum with nearby hues ────────────────────────────

/**
 * If the extracted spectrum has fewer than `minCount` colors, pads with
 * nearby similar colors (shift hue ±10°, ±20°, adjust saturation ±15%)
 * to give the user more precise choice.
 */
export function padSpectrum(spectrum, minCount = 6) {
  if (!spectrum || spectrum.length === 0) return spectrum;
  if (spectrum.length >= minCount) return spectrum;

  const padded = [...spectrum];
  const shifts = [
    { dh: 10,  ds: 0,   dl: 0 },
    { dh: -10, ds: 0,   dl: 0 },
    { dh: 20,  ds: 0,   dl: 0 },
    { dh: -20, ds: 0,   dl: 0 },
    { dh: 0,   ds: 15,  dl: -5 },
    { dh: 0,   ds: -15, dl: 5 },
    { dh: 10,  ds: 10,  dl: -3 },
    { dh: -10, ds: -10, dl: 3 },
  ];

  for (const base of spectrum) {
    if (padded.length >= minCount) break;
    for (const shift of shifts) {
      if (padded.length >= minCount) break;
      const h = ((base.h + shift.dh) % 360 + 360) % 360;
      const s = Math.max(5, Math.min(100, base.s + shift.ds));
      const l = Math.max(10, Math.min(90, base.l + shift.dl));
      const hex = hslToHex(h, s, l);
      // Avoid duplicates
      if (!padded.some(c => c.hex === hex)) {
        const rgb = hexToRgb(hex);
        padded.push({ hex, ...rgb, h, s, l, vibrancy: vibrancy(s, l) });
      }
    }
  }

  return padded.slice(0, Math.max(minCount, spectrum.length));
}

// ─── Build palette from extracted spectrum ───────────────────────────

/**
 * Builds a complete palette from an array of extracted colors.
 *
 * Uses the actual extracted hues directly (no snapping to Tailwind families).
 * The most vibrant color becomes `accent`, the second becomes `accentSoft`.
 *
 * @param {Array<{hex,r,g,b,h,s,l,vibrancy}>} spectrum — extracted colors (vibrancy-sorted)
 * @param {Object} elements — element overrides from getIntensityElements()
 * @returns {Object} — palette colors ready for paletteCustom
 */
export function buildPaletteFromSpectrum(spectrum, elements = {}) {
  if (!spectrum || spectrum.length === 0) return null;

  // Filter out very dark colors (l < 12) for accent selection
  const vibrant = spectrum.filter(c => c.l > 12 && c.s > 5);
  const usable = vibrant.length > 0 ? vibrant : spectrum;

  // Primary accent = most vibrant (already sorted)
  const primary = usable[0];

  // Secondary accent = second most vibrant, preferring a different hue
  let secondary = usable.length > 1 ? usable[1] : primary;
  // If we have 3+ colors, pick the one with the most hue distance from primary
  if (usable.length >= 3) {
    let bestDist = 0;
    for (let i = 1; i < usable.length; i++) {
      const hueDist = Math.min(
        Math.abs(usable[i].h - primary.h),
        360 - Math.abs(usable[i].h - primary.h)
      );
      if (hueDist > bestDist) {
        bestDist = hueDist;
        secondary = usable[i];
      }
    }
  }

  // Brighten accents if they're too dark for visibility on dark backgrounds
  const accent = brightenForAccent(primary);
  const accentSoft = brightenForAccent(secondary, -20); // slightly softer

  // Compute average luminance of entire spectrum (not just vibrant)
  const avgL = spectrum.reduce((s, c) => s + c.l, 0) / spectrum.length;

  // Dark backgrounds (most common) → white text
  const isDark = avgL < 50;
  const textBase = isDark ? { r: 255, g: 255, b: 255 } : { r: 15, g: 23, b: 42 };
  const textHex = isDark ? '#ffffff' : '#0f172a';
  const glassTint = isDark ? '#ffffff' : '#0f172a';

  return {
    textPrimary:  textHex,
    textMuted:    `rgba(${textBase.r},${textBase.g},${textBase.b},0.75)`,
    textSubtle:   `rgba(${textBase.r},${textBase.g},${textBase.b},0.55)`,
    accent:       accent,
    accentSoft:   accentSoft,
    glassTint:    glassTint,
    surface:      isDark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.20)',
    _elements:    elements,
    _spectrum:    spectrum.slice(0, 5).map(c => c.hex), // Store for UI display
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * Bumps a color to the 300-500 lightness range if it's too dark,
 * so it works as a visible accent on both dark and light backgrounds.
 */
function brightenForAccent(color, lightShift = 0) {
  let { h, s, l } = color;
  // Target: 45–65% lightness for accents
  const targetL = 55 + lightShift / 5;
  if (l < 35) {
    l = Math.min(65, targetL);
  } else if (l > 80) {
    l = Math.max(45, targetL);
  }
  // Boost saturation for more punch
  s = Math.min(100, Math.max(s, 50));
  return hslToHex(h, s, l);
}

export function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360;
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// ─── Build palette from a base hue + harmony type ────────────────────

/**
 * Builds a palette from a specific base hue using color harmony theory.
 * Works for both manual Tailwind family selection and spectrum-picked colors.
 *
 * @param {number} baseHue — 0-360 hue angle
 * @param {string} harmonyType — harmony type id
 * @param {Object} elements — element overrides from getIntensityElements()
 * @param {number} [baseSat=75] — base saturation (for spectrum colors)
 * @param {number} [baseLight=55] — base lightness target
 * @returns {Object} — palette colors ready for paletteCustom
 */
// ─── Extract dominant color from an image URL ────────────────────────

/**
 * Loads an image from URL and extracts the most dominant color.
 * Returns { hex, r, g, b, h, s, l, vibrancy } or null on failure.
 *
 * Session 62: accepts optional assetId for presigned URL refresh on failure.
 *
 * @param {string} url — image URL (presigned S3 URL, etc.)
 * @param {string} [assetId] — optional asset_id for URL refresh on expiry
 * @returns {Promise<{hex,r,g,b,h,s,l,vibrancy}|null>}
 */
export async function extractDominantFromUrl(url, assetId) {
  // Attempt 1
  try {
    const img = await loadImage(url);
    const raw = extractFromCanvas(img);
    if (raw.length === 0) return null;
    return raw.sort((a, b) => b.vibrancy - a.vibrancy)[0];
  } catch (e) {
    // On failure: try refreshing the presigned URL once
    if (assetId) {
      const freshUrl = await refreshPresignedUrl(assetId);
      if (freshUrl && freshUrl !== url) {
        try {
          const img = await loadImage(freshUrl);
          const raw = extractFromCanvas(img);
          if (raw.length === 0) return null;
          return raw.sort((a, b) => b.vibrancy - a.vibrancy)[0];
        } catch {
          // Retry failed — give up silently
          return null;
        }
      }
    }
    // No assetId or refresh failed — give up silently (no console spam)
    return null;
  }
}

// ─── Compute a contrastive accent from a dominant color ──────────────

/**
 * Computes an accent color that is visually contrastive (complementary hue)
 * to the given dominant color. The result is bright and saturated enough
 * to be clearly visible on the dark (#1a1a1c) instrument-panel UI.
 *
 * @param {string} hex — dominant color hex (e.g. '#2a5a3a')
 * @returns {string} — contrastive accent hex (e.g. '#e65baa')
 */
export function getContrastiveAccent(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#34d399'; // fallback to emerald
  const hsl = hexToHsl(hex);
  if (!hsl) return '#34d399';

  // Complementary hue (180° shift)
  const contrastHue = (hsl.h + 180) % 360;

  // Ensure saturation is punchy (at least 60%) and lightness is in the
  // "LED visible" range (50-62%) so it pops on the dark panel background
  const s = Math.min(100, Math.max(60, hsl.s + 15));
  const l = 58;

  return hslToHex(contrastHue, s, l);
}

/**
 * Build a palette from a base hue + harmony type.
 *
 * Four distinct code paths based on chromatic × harmony type:
 *
 * 1. Chromatic Mono  — same hue, vary saturation + lightness (Dir ≠ Har)
 * 2. Achromatic Mono — pure grayscale (sat = 0), vary lightness only
 * 3. Achromatic non-Mono — subtly tinted base color per column
 * 4. Chromatic non-Mono — full harmony colours (standard path)
 *
 * @param {number} baseHue
 * @param {string} harmonyType
 * @param {object} elements — element override map
 * @param {number} baseSat — saturation for palette generation (may be boosted)
 * @param {number} baseLight — lightness for palette generation (clamped 50-78)
 * @param {boolean} anchorToBase — true: title stays on base; false: full spread
 * @param {number|null} realSat — actual saturation of the chosen colour
 * @param {string|null} originalHex — the EXACT hex of the chosen colour
 */
export function buildPaletteFromHue(
  baseHue, harmonyType, elements = {},
  baseSat = 75, baseLight = 55, anchorToBase = false,
  realSat = null, originalHex = null,
) {
  const hues = generateHarmony(baseHue, harmonyType);
  const isMono = harmonyType === 'monochromatic';
  const effectiveRealSat = realSat !== null ? realSat : baseSat;
  const isAchromatic = effectiveRealSat < 15;

  // ── For achromatic palettes, infer text polarity from the base colour itself.
  //    White (high luminance) → user has dark background → white text.
  //    Black/Gray (low luminance) → user has light background → dark text.
  //    Chromatic palettes always use white text (dark bg assumed). ──
  let darkText = false;
  if (isAchromatic && originalHex) {
    const baseLum = luminance(originalHex);
    darkText = baseLum < 0.5;   // black → 0, gray ~0.37 → dark text; white → 1 → light text
  }
  const textR = darkText ? 30  : 255;
  const textG = darkText ? 30  : 255;
  const textB = darkText ? 46  : 255;

  let accent, accentSoft;

  if (isMono && isAchromatic) {
    // ── Achromatic Mono ──
    // Dir (anchorToBase): accent = accentSoft = EXACT base colour. Full stop.
    // Har (!anchorToBase): grayscale spread (named presets handled by PalettePicker).
    const base = originalHex || hslToHex(0, 0, baseLight);
    if (anchorToBase) {
      accent    = base;
      accentSoft = base;
    } else {
      // Harmony row: spread lightness for visual variety
      if (darkText) {
        // Light bg context (black/gray base): title dark, body slightly lighter
        accent    = base;
        accentSoft = hslToHex(0, 0, Math.min(55, baseLight + 20));
      } else {
        // Dark bg context (white base): title white, body slightly dimmer
        accent    = base;
        accentSoft = hslToHex(0, 0, Math.max(55, baseLight - 20));
      }
    }

  } else if (isMono) {
    // ── Chromatic Mono — same hue, exact base colour ──
    const exactBase = originalHex || hslToHex(baseHue, Math.min(100, baseSat + 5), baseLight);
    if (anchorToBase) {
      accent    = exactBase;
      accentSoft = exactBase;
    } else {
      accent    = exactBase;
      accentSoft = hslToHex(baseHue, Math.max(30, baseSat - 30), Math.max(30, baseLight - 18));
    }

  } else if (isAchromatic) {
    // ── Achromatic + non-Mono — subtly tinted base colour per column ──
    //
    // Instead of bright saturated accents, we "tint" the base shade:
    //   • WHITE base (dark bg): soft pastels — cream, ice blue, mint …
    //   • BLACK base (light bg): deep tinted darks — navy, forest, wine …
    //   • GRAY  base (light bg): muted tinted grays — warm gray, slate, sage …
    //
    // The tints are subtle enough to feel achromatic-adjacent, not chromatic.
    const COLUMN_ACCENTS = {
      'analogous':           45,   // Gold
      'complementary':       199,  // Blue
      'split-complementary': 152,  // Green
      'triadic':             258,  // Violet
      'tetradic':            0,    // Rose
      'square':              25,   // Orange
    };
    const columnHue = COLUMN_ACCENTS[harmonyType];
    if (columnHue !== undefined) {
      const baseLum = originalHex ? luminance(originalHex) : 0.5;
      const neutral = originalHex || hslToHex(0, 0, baseLight);

      let tintAccent, tintSoft;

      if (baseLum > 0.7) {
        // ── WHITE base — near-white tints on dark background ──
        tintAccent = hslToHex(columnHue, 22, 82);
        tintSoft   = hslToHex(columnHue, 16, 72);
      } else if (baseLum < 0.08) {
        // ── BLACK base — Paper-quality tints (slate-like tonal depth) ──
        // Reference: Paper uses slate-700 (L≈33) → slate-500 (L≈47).
        tintAccent = hslToHex(columnHue, 22, 33);  // dark, clear tint (≈ slate-700)
        tintSoft   = hslToHex(columnHue, 16, 47);  // medium, softer tint (≈ slate-500)
      } else {
        // ── GRAY base — muted tinted grays ──
        tintAccent = hslToHex(columnHue, 20, 38);
        tintSoft   = hslToHex(columnHue, 14, 52);
      }

      if (anchorToBase) {
        // Dir row: both title and body use the tint (uniform single shade)
        accent    = tintAccent;
        accentSoft = tintAccent;
      } else {
        // Har/Acc rows: two-tone tint spread (richer variation)
        accent    = tintAccent;
        accentSoft = tintSoft;
      }
    } else {
      const neutral = originalHex || hslToHex(0, 0, baseLight);
      accent    = neutral;
      accentSoft = neutral;
    }

  } else {
    // ── Standard chromatic harmony ──
    const accentHue = anchorToBase ? baseHue : (hues.length > 1 ? hues[1] : hues[0]);
    const softHue = anchorToBase
      ? (hues.length > 1 ? hues[1] : hues[0])
      : (hues.length > 2 ? hues[2] : hues[0]);
    accent    = hslToHex(accentHue, Math.min(100, baseSat + 10), baseLight);
    accentSoft = hslToHex(softHue, Math.max(40, baseSat - 10), baseLight + 5);
  }

  // textPrimary MUST be hex — computeRenderColors passes it through hexToRgb().
  const textHex = darkText ? '#1e1e2e' : '#ffffff';

  return {
    textPrimary:  textHex,
    textMuted:    `rgba(${textR},${textG},${textB},0.75)`,
    textSubtle:   `rgba(${textR},${textG},${textB},0.55)`,
    accent,
    accentSoft,
    glassTint:    darkText ? '#000000' : '#ffffff',
    surface:      darkText ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)',
    _elements:    elements,
  };
}
